<template>
  <v-card :loading="loading" variant="outlined">
    <v-card-title class="text-subtitle-1">
      <v-icon class="mr-2">mdi-chart-bar</v-icon>
      Statistici Servicii Client
    </v-card-title>
    <v-card-text>
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="!hasStats" class="text-center text-grey py-4">
        Nu exista servicii pentru acest client.
      </div>

      <div v-else>
        <!-- Summary row -->
        <v-row class="mb-3">
          <v-col cols="12">
            <v-chip color="primary" class="mr-2">
              Total: {{ totalServices }}
            </v-chip>
            <v-chip color="success" class="mr-2" v-if="totalActive > 0">
              Active: {{ totalActive }}
            </v-chip>
            <v-chip color="warning" class="mr-2" v-if="totalPending > 0">
              Pending: {{ totalPending }}
            </v-chip>
          </v-col>
        </v-row>

        <!-- Stats by service type -->
        <div v-for="(typeStats, serviceType) in statsByType" :key="serviceType" class="mb-3">
          <div class="d-flex align-center mb-1">
            <v-icon size="small" class="mr-2">mdi-tag</v-icon>
            <span class="font-weight-medium">{{ serviceType }}</span>
            <v-chip size="x-small" class="ml-2">{{ typeStats.total }}</v-chip>
          </div>

          <div class="pl-6">
            <v-chip
                v-for="(count, status) in typeStats.statuses"
                :key="status"
                :color="getStatusColor(status)"
                size="small"
                class="mr-1 mb-1"
                variant="outlined"
            >
              {{ status }}: {{ count }}
            </v-chip>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { log } from "@/composables/useLog";
import { getClientServicesStats } from "@/composables/useGetClientServicesAPI";

export default {
  name: 'ClientServicesStats',
  props: {
    clientId: {
      type: [Number, String],
      required: true
    },
    token: {
      type: String,
      required: true
    },
    includeSubclients: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      stats: null
    };
  },
  computed: {
    hasStats() {
      return this.stats && Object.keys(this.stats).length > 0;
    },
    statsByType() {
      if (!this.stats) return {};
      return this.stats;
    },
    totalServices() {
      if (!this.stats) return 0;
      let total = 0;
      Object.values(this.stats).forEach(typeStats => {
        total += typeStats.total || 0;
      });
      return total;
    },
    totalActive() {
      if (!this.stats) return 0;
      let total = 0;
      Object.values(this.stats).forEach(typeStats => {
        total += typeStats.statuses?.active || 0;
      });
      return total;
    },
    totalPending() {
      if (!this.stats) return 0;
      let total = 0;
      Object.values(this.stats).forEach(typeStats => {
        total += typeStats.statuses?.pending_payment || 0;
      });
      return total;
    }
  },
  methods: {
    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'pending_payment': 'info',
        'expired': 'warning',
        'suspended': 'orange',
        'terminated': 'error',
        'refunded': 'grey',
        'deleted': 'grey'
      };
      return colors[status] || 'grey';
    },

    async loadStats() {
      console.log("ClientServicesStats loadStats called, clientId=" + this.clientId + ", token=" + (this.token ? "exists" : "null"));
      if (!this.clientId || !this.token) {
        console.log("ClientServicesStats loadStats - missing clientId or token, returning");
        return;
      }

      this.loading = true;
      log("Loading client services stats for client_id=" + this.clientId);

      try {
        const response = await getClientServicesStats(this.token, this.clientId, this.includeSubclients);

        if (!response.error) {
          this.stats = response.stats || {};
          log("Loaded stats:");
          log(this.stats);
        } else {
          log("Error loading stats: " + response.message);
        }
      } catch (err) {
        log("Exception loading stats:");
        log(err);
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    console.log("ClientServicesStats MOUNTED with clientId=" + this.clientId + ", token=" + (this.token ? "exists" : "null"));
    // Ensure we load stats on mount if both props are available
    if (this.clientId && this.token) {
      this.loadStats();
    }
  },
  watch: {
    clientId: {
      handler(newVal) {
        console.log("ClientServicesStats clientId watcher fired, newVal=" + newVal);
        this.loadStats();
      },
      immediate: true
    },
    token: {
      handler(newToken) {
        console.log("ClientServicesStats token watcher fired, token=" + (newToken ? "exists" : "null"));
        if (newToken && this.clientId) {
          this.loadStats();
        }
      }
    }
  }
};
</script>
